// @flow
import React, { useContext, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { constants } from '@clubmed/utils';

import { isJoyview } from 'Components/utils/product/product.utils';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { AFTER_APP } from 'Components/frameworks/portal/Portal.constants';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  useCriteriaDisplayContext,
  CriteriaDisplayProvider,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { PriceRemoteContent } from 'Components/widgets/price-remote/price-remote-content/PriceRemoteContent';
import { PriceRemoteDetails } from 'Components/widgets/price-remote/price-remote-details/PriceRemoteDetails.jsx';
import { PriceRemoteDetailsLayer } from 'Components/widgets/price-remote/price-remote-details-layer/PriceRemoteDetailsLayer';
import { PriceRemoteTitleButton } from 'Components/widgets/price-remote/price-remote-title-button/PriceRemoteTitleButton';
import { PriceRemoteContext } from 'Components/widgets/price-remote/PriceRemoteContext';
import ReactAppContext from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { setAdultsNumber } from 'Store/trip-information/adults/adults.action';
import { getCalendarLoading } from 'Store/calendar/calendar.selector';
import { type PriceOfStayData } from 'Store/price-remote/price-of-stay/priceOfStay.helper';
import { resetPriceOfStay } from 'Store/price-remote/price-of-stay/priceOfStay.actions';
import { type AllInclusiveServicesData } from 'Store/price-remote/all-inclusive-services/allInclusiveServices.helper';
import { getAllInclusiveServices } from 'Store/price-remote/all-inclusive-services/allInclusiveServices.actions';
import { allInclusiveServicesSelector } from 'Store/price-remote/all-inclusive-services/allInclusiveServices.selector';
import { TrackClick } from 'Tracking/track-click/TrackClick';

const { ALL_INCLUSIVE_SERVICES_TYPES } = constants;

export type Props = {
  product: Resort,
  bookingCriteria: BookingCriteria,
  isSticky: boolean,
  urgentisationRate: number,
};

export const PriceRemote = (props: Props) => {
  const appContext = useContext(ReactAppContext);
  const { isDesktop } = useContext(ResponsiveContext);
  const [{ isDetailOpened }, setPriceRemoteContext] = useContext(PriceRemoteContext);
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext] = useCriteriaDisplayContext();

  const dispatch = useDispatch();

  const allInclusiveServices = useSelector<AllInclusiveServicesData>(allInclusiveServicesSelector);
  const allInclusiveServicesIsLoading = useSelector<boolean>(
    state => state.priceRemote.allInclusiveServices.isLoading,
  );
  const adultsNumber: ?number = useSelector(state => state.tripInformation.adults.length);

  const { priceOfStay, priceIsLoading } = useSelector<{
    priceOfStay: PriceOfStayData,
    priceIsLoading: boolean,
  }>(state => {
    const {
      data: priceOfStayData,
      isLoading: priceOfStayIsLoading,
    } = state.priceRemote.priceOfStay;

    return {
      priceOfStay: priceOfStayData || {},
      priceIsLoading:
        (!priceOfStayData && getCalendarLoading(state)) ||
        (priceOfStayData && priceOfStayIsLoading),
    };
  });

  const filteredAllInclusiveServices = criteriaContext
    ? allInclusiveServices.filter(({ type }) => {
        switch (type) {
          case ALL_INCLUSIVE_SERVICES_TYPES.TRANSPORT:
            return criteriaContext.criteriaTransportIsSet;
          case ALL_INCLUSIVE_SERVICES_TYPES.CHILDCARE:
            return criteriaContext.criteriaWhoIsSet;
          default:
            return true;
        }
      })
    : allInclusiveServices;

  const { product, isSticky, bookingCriteria, urgentisationRate } = props;

  const isStickyAndIsMobile = isSticky && !isDesktop;
  const isStickyAndIsDesktop = isSticky && isDesktop;
  const isJoyView = isJoyview(product.type);

  useEffect(
    () => {
      setPriceRemoteContext({ isJoyview: isJoyView });
      setCriteriaContext({ isJoyview: isJoyView });
    },
    [isJoyView],
  );

  useEffect(
    () => {
      setPriceRemoteContext({ isCompact: isSticky && !isDetailOpened });
    },
    [isSticky, isDetailOpened],
  );

  useEffect(
    () => {
      if (isSticky && isDesktop) {
        setPriceRemoteContext({ isDetailOpened: !isSticky });
      }
    },
    [isSticky],
  );

  useEffect(() => {
    if (!adultsNumber) {
      dispatch(setAdultsNumber(DEFAULT_ADULTS_NB));
    }

    return () => {
      dispatch(resetPriceOfStay());
    };
  }, []);

  useEffect(
    () => {
      const isOpen =
        criteriaDisplayContext.criteriaWhenIsOpen ||
        criteriaDisplayContext.criteriaWhoIsOpen ||
        criteriaDisplayContext.criteriaTransportIsOpen;

      setPriceRemoteContext({ isOverrideSticky: !isOpen });
      if (isOpen) {
        scrollToElement('.PriceRemoteStickyWrapper-initialPosition');
      }
    },
    [
      criteriaDisplayContext.criteriaWhenIsOpen,
      criteriaDisplayContext.criteriaWhoIsOpen,
      criteriaDisplayContext.criteriaTransportIsOpen,
    ],
  );

  useEffect(
    () => {
      if (product.id) {
        dispatch(
          getAllInclusiveServices(appContext, {
            accommodationId: priceOfStay.accommodationId,
            accommodationLabel: priceOfStay.accommodationLabel,
            productId: product.id,
            includesTransportation: priceOfStay.includesTransportation,
            includesChildcare: priceOfStay.includesChildcare,
          }),
        );
      }
    },
    [
      priceOfStay.accommodationId,
      priceOfStay.accommodationLabel,
      priceOfStay.includesTransportation,
      priceOfStay.includesChildcare,
      product.id,
    ],
  );

  const isLoading = priceIsLoading || allInclusiveServicesIsLoading;

  return (
    <SiteContainer
      className={classnames({
        'mb-2 mt-8': isStickyAndIsDesktop,
        'slideInBottom border-t-1 border-gray-light bg-white': isStickyAndIsMobile,
      })}
    >
      <div
        className={classnames('bg-white relative', {
          'border-1 rounded-small shadow-sm': !isStickyAndIsMobile,
        })}
      >
        <Fragment>
          <PriceRemoteContent
            bookingCriteria={bookingCriteria}
            classNames={classnames('flex-wrap p-1 lg:p-3 lg:flex', {
              'p-2': !isDesktop && !isSticky,
              flex: !isDesktop && !isDetailOpened,
              'lg:flex-no-wrap': isSticky && !isDetailOpened,
            })}
            isSticky={isSticky}
            product={product}
            urgentisationRate={urgentisationRate}
          />
          {isDesktop && isDetailOpened ? (
            <PriceRemoteDetails
              allInclusiveServices={filteredAllInclusiveServices}
              isSticky={isSticky}
              isLoading={isLoading}
            />
          ) : null}
          <div
            className={classnames('flex items-center justify-center', {
              'absolute left-1/2 -translate-x-50': !isSticky || (isSticky && isDesktop),
              'bottom-full': isSticky && isDesktop,
            })}
          >
            <PriceRemoteTitleButton
              isDesktop={isDesktop}
              isJoyview={isJoyView}
              isSticky={isSticky}
              isCollapsed={isDetailOpened}
              handleClick={() => {
                if (!isDetailOpened && !isSticky) {
                  scrollToElement('.PriceRemoteStickyWrapper-initialPosition');
                }
                setPriceRemoteContext({ isDetailOpened: !isDetailOpened });
              }}
            />
          </div>
        </Fragment>
        {!isDesktop && isDetailOpened ? (
          <PriceRemoteDetailsLayer
            allInclusiveServices={filteredAllInclusiveServices}
            closeLabel={t('common.close')}
            isLoading={isLoading}
            nameRender={AFTER_APP}
            onBack={() => setPriceRemoteContext({ isDetailOpened: false })}
          >
            <CriteriaDisplayProvider>
              <PriceRemoteContent
                bookingCriteria={bookingCriteria}
                classNames="py-5 bg-white w-full mt-auto"
                isSticky={isSticky}
                product={product}
                urgentisationRate={urgentisationRate}
              />
            </CriteriaDisplayProvider>
          </PriceRemoteDetailsLayer>
        ) : null}
        <div className="absolute flex w-full mt-10 justify-center lg:block lg:w-auto lg:rtl:right-0 lg:mt-1">
          {!(isDetailOpened || isSticky) && (
            <TrackClick
              className="bg-transparent text-center text-blue underline text-base font-bold"
              trackingData={{
                event: 'eventGA',
                categoryGA: 'resort',
                actionGA: 'priceBar',
                labelGA: 'checkPrices',
                valueGA: '0',
                nonInteraction: '0',
              }}
              as={DynamicLink}
              url="#booking"
            >
              {t('priceRemote.linkToCalendar')}
            </TrackClick>
          )}
        </div>
      </div>
    </SiteContainer>
  );
};
